import { resolveSourceTypeLabel } from 'flare-ui-primitives/source-type-terminology';
import { formatExecutionDetail } from './thinkingProcessDetail.js';
import { workflowRunEvents } from './workflowRunProjectionState.js';

const RESOURCE_LABEL_FIELDS = [
  'display_title',
  'file_name',
  'filename',
  'name',
  'title',
  'source_name',
];
const STATUS_ALIASES = new Map([
  ['accepted', 'completed'],
  ['succeeded', 'completed'],
  ['done', 'completed'],
  ['finished', 'completed'],
]);
const TECHNICAL_ACTIVITY_SUMMARIES = new Set([
  ...STATUS_ALIASES.keys(),
  'blocked',
  'cancelled',
  'canceled',
  'completed',
  'failed',
  'partial',
  'pending',
  'running',
  'waiting_for_user',
  'waiting_user',
]);

const normalizeText = (value = '') => String(value || '').trim();
const normalizeLower = (value = '') => normalizeText(value).toLowerCase();
const resolveObject = (value = null) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);
const resolveArray = (value = null) => (Array.isArray(value) ? value : []);
const compactLineParts = (items = []) => items.map(normalizeText).filter(Boolean).join(' · ');
const firstText = (values = []) => values.map(normalizeText).find(Boolean) || '';
const normalizeStatus = (value = '') => {
  const status = normalizeLower(value);
  return STATUS_ALIASES.get(status) || status || 'running';
};

export const visibleActivityDetail = (value = '') => {
  const detail = normalizeText(value);
  return TECHNICAL_ACTIVITY_SUMMARIES.has(normalizeLower(detail)) ? '' : detail;
};

export function resourceLabel(item) {
  if (typeof item === 'string') return normalizeText(item);
  const source = resolveObject(item);
  const resourceRef = resolveObject(source.resource_ref);
  const field = RESOURCE_LABEL_FIELDS.find((key) => normalizeText(resourceRef[key] || source[key]));
  return field ? normalizeText(resourceRef[field] || source[field]) : '';
}

function sourceTypeLabel(item = {}) {
  const source = resolveObject(item);
  const resourceRef = resolveObject(source.resource_ref);
  const sourceType = normalizeLower(resourceRef.source_type || source.source_type || source.source);
  return sourceType ? resolveSourceTypeLabel(sourceType) : '';
}

function sourceStatusLabel(item = {}) {
  const source = resolveObject(item);
  const resourceRef = resolveObject(source.resource_ref);
  const admission = resolveObject(source.evidence_admission);
  if (admission.state === 'admitted' || resourceRef.selected === true || resourceRef.linked === true) {
    return '已采用';
  }
  if (normalizeLower(resourceRef.status) === 'available') return '可用';
  if (normalizeLower(resourceRef.status) === 'missing') return '不可用';
  return '';
}

export function formatSourceLine(item = {}) {
  const title = resourceLabel(item);
  return title ? compactLineParts(['来源', sourceTypeLabel(item), title, sourceStatusLabel(item)]) : '';
}

export function formatSourceLines(items = []) {
  return [...new Set(resolveArray(items).map(formatSourceLine).filter(Boolean))];
}

function primaryResourceAction(item = {}) {
  const source = resolveObject(item);
  const resourceRef = resolveObject(source.resource_ref);
  const actions = resolveArray(resourceRef.actions);
  return actions.find((action) => resolveObject(action).primary === true) || actions[0] || null;
}

export function formatResourceEntries(items = []) {
  return resolveArray(items)
    .map((item) => ({
      line: formatSourceLine(item),
      label: resourceLabel(item),
      meta: compactLineParts([sourceTypeLabel(item), sourceStatusLabel(item)]),
      action: primaryResourceAction(item),
    }))
    .filter(({ line }) => Boolean(line));
}

const traceIdentity = (trace = {}) => {
  const result = resolveObject(trace.node_result);
  return normalizeText(result.node_key || trace.step_id || trace.node_id || trace.label);
};
const owningStepId = (trace = {}) => {
  const result = resolveObject(trace.node_result);
  const metadata = resolveObject(result.metadata);
  return normalizeText(metadata.workflow_step_id || trace.workflow_step_id || trace.step_id);
};
const traceLabel = (trace = {}) => {
  const result = resolveObject(trace.node_result);
  const metadata = resolveObject(result.metadata);
  const publicLabel = normalizeText(metadata.public_label);
  if (publicLabel) return publicLabel;
  const label = normalizeText(trace.label);
  return label && !/^[a-z0-9_:-]+$/i.test(label) ? label : '执行步骤';
};
const traceKind = (trace = {}) => (
  resolveArray(trace.sources).length > 0 ? 'resource_read' : 'capability'
);

function latestTraceRecords(records = []) {
  const order = [];
  const traces = resolveArray(records).reduce((index, trace) => {
    const identity = traceIdentity(trace) || `trace:${order.length}`;
    if (!index.has(identity)) order.push(identity);
    index.set(identity, trace);
    return index;
  }, new Map());
  return order.map((identity) => traces.get(identity));
}

function traceActivities(trace = {}, skillId = '') {
  const result = resolveObject(trace.node_result);
  const resources = formatResourceEntries(trace.sources);
  const primary = {
    key: `trace:${traceIdentity(trace)}`,
    kind: traceKind(trace),
    label: traceLabel(trace),
    detail: visibleActivityDetail(formatExecutionDetail(trace.detail || result.content || '')),
    status: normalizeStatus(trace.status || result.status),
    resources,
    skillId: normalizeText(skillId),
    target_id: normalizeText(skillId),
  };
  const patches = resolveArray(result.patches).map((patch, index) => {
    const record = resolveObject(patch);
    return {
      key: `patch:${traceIdentity(trace)}:${index}`,
      kind: 'artifact_update',
      label: normalizeText(record.label) || '更新结构化结果',
      detail: visibleActivityDetail(record.summary),
      status: normalizeStatus(trace.status || result.status),
      resources: [],
    };
  });
  return [primary, ...patches];
}

const artifactTargetComplete = (artifactTarget = {}) => [
  artifactTarget.project_id,
  artifactTarget.artifact_id,
  artifactTarget.artifact_version_id,
  artifactTarget.artifact_type,
  artifactTarget.content_digest,
].every((value) => Boolean(normalizeText(value)));

const artifactResources = (title, artifactTarget) => (
  artifactTargetComplete(artifactTarget) ? [{
    line: compactLineParts(['产物', title, '已生成']),
    label: title,
    meta: compactLineParts(['工作文档', '已生成']),
    action: {
      type: 'open_artifact_document',
      label: '预览产物',
      primary: true,
      artifact_target: artifactTarget,
    },
  }] : []
);

function artifactAdmissionActivity(payload = {}) {
  const stepId = firstText([payload.step_id, payload.stepId]);
  if (!stepId) return null;
  const title = firstText([payload.artifact_title, payload.title, payload.label]);
  const artifactTarget = resolveObject(payload.artifact_target);
  return {
    stepId,
    activity: {
      key: `artifact:${firstText([artifactTarget.artifact_id, payload.artifact_id])}:${firstText([artifactTarget.artifact_version_id, 'accepted'])}`,
      kind: 'artifact_admission',
      label: compactLineParts(['接纳产物', title]),
      detail: '',
      status: normalizeStatus(payload.status || 'completed'),
      resources: artifactResources(title, artifactTarget),
    },
  };
}

function decisionActivity(event = {}, payload = {}) {
  const stepId = normalizeText(payload.step_id || payload.stepId);
  const acceptedLabel = normalizeText(payload.accepted_label);
  if (!stepId || !acceptedLabel) return null;
  return {
    stepId,
    activity: {
      key: `event:${event.sequence}:confirmation`,
      kind: 'decision',
      label: compactLineParts(['接纳选择', acceptedLabel]),
      detail: '',
      status: 'completed',
      resources: [],
    },
  };
}

const EVENT_ACTIVITY_PROJECTORS = new Map([
  ['artifact.admitted', (_event, payload) => artifactAdmissionActivity(payload)],
  ['workstep.completed', decisionActivity],
]);

function eventActivity(event = {}) {
  const projector = EVENT_ACTIVITY_PROJECTORS.get(normalizeText(event.event_name));
  return projector?.(event, resolveObject(event.payload)) ?? null;
}

function activitySummary(activities = []) {
  const executionCount = activities.filter(({ kind }) => ['resource_read', 'capability'].includes(kind)).length;
  const resourceCount = activities
    .filter(({ kind }) => kind === 'resource_read')
    .reduce((count, activity) => count + activity.resources.length, 0);
  const updateCount = activities.filter(({ kind }) => ['artifact_update', 'artifact_admission'].includes(kind)).length;
  const decisionCount = activities.filter(({ kind }) => kind === 'decision').length;
  return compactLineParts([
    executionCount ? `执行 ${executionCount} 项操作` : '',
    resourceCount ? `读取 ${resourceCount} 份资料` : '',
    updateCount ? `更新 ${updateCount} 项结果` : '',
    decisionCount ? `确认 ${decisionCount} 项选择` : '',
  ]);
}

const capabilityByStep = (events = []) => resolveArray(events).reduce((index, event) => {
  if (normalizeText(event.event_name) !== 'capability.called') return index;
  const payload = resolveObject(event.payload);
  const stepId = normalizeText(payload.step_id || payload.stepId);
  const capabilityId = normalizeText(payload.capability_id);
  if (stepId && capabilityId) index.set(stepId, capabilityId);
  return index;
}, new Map());

export const skillByCapability = (projection = {}) => {
  const claims = resolveArray(resolveObject(projection).skills)
    .filter(record => record?.target_type === 'skill' && record.registered === true
      && normalizeText(record.target_id))
    .flatMap(record => resolveArray(record.capability_ids).map(normalizeText).filter(Boolean)
      .map(capabilityId => [capabilityId, normalizeText(record.target_id)]))
    .reduce((index, [capabilityId, skillId]) => {
      const owners = index.get(capabilityId) ?? new Set();
      owners.add(skillId);
      index.set(capabilityId, owners);
      return index;
    }, new Map());
  return new Map([...claims].filter(([, owners]) => owners.size === 1)
    .map(([capabilityId, owners]) => [capabilityId, owners.values().next().value]));
};

const dedupeActivities = (activities = []) => [...resolveArray(activities).reduce((index, activity) => {
  index.set(activity.key, activity);
  return index;
}, new Map()).values()];

export function resolveThinkingProcessActivityEntries(item = {}, skillProjection = {}) {
  const runEvents = workflowRunEvents(item);
  const capabilityIndex = capabilityByStep(runEvents);
  const skillIndex = skillByCapability(skillProjection);
  const traceEntries = latestTraceRecords(item.executionCards).flatMap((trace) => {
    const stepId = owningStepId(trace);
    const capabilityId = capabilityIndex.get(stepId) || normalizeText(trace.capability_id);
    const skillId = skillIndex.get(capabilityId) || '';
    return stepId ? traceActivities(trace, skillId).map((activity) => ({ stepId, activity })) : [];
  });
  const eventEntries = runEvents.map(eventActivity).filter(Boolean);
  const grouped = [...traceEntries, ...eventEntries].reduce((index, { stepId, activity }) => {
    index.set(stepId, dedupeActivities([...(index.get(stepId) ?? []), activity]));
    return index;
  }, new Map());
  return [...grouped.entries()].map(([stepId, activities]) => ({
    stepId,
    summary: activitySummary(activities),
    activities,
  }));
}
